import React from 'react';
import { ShieldCheckIcon, HeartIcon, AwardIcon, UsersIcon, PlayCircleIcon, MapPinIcon } from 'lucide-react';
export function TrustSection() {
  return <section id="why-us" className="w-full bg-soft-teal py-16 lg:py-24" aria-label="Proudly Canadian and Nurse-Owned trust section" data-id="element-815">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-816">
        {/* Section header */}
        <div className="text-center mb-14" data-id="element-817">
          <div className="inline-flex items-center gap-2 bg-white text-medical-blue text-xs font-semibold px-3 py-1.5 rounded-full mb-4 shadow-sm" data-id="element-818">
            🍁 Canadian Owned & Operated
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-text-dark tracking-tight mb-4" data-id="element-819">
            Staffing, Care &{' '}
            <span className="text-medical-blue" data-id="element-820">Operations</span>
          </h2>
          <p className="text-text-muted text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-821">
            Built by nurses, for healthcare. Care Nomad provides a complete
            ecosystem to solve the staffing crisis, deliver quality home care,
            and optimize facility operations.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-16" data-id="element-822">
          {/* Left: Founder video bio */}
          <div className="relative" data-id="element-823">
            <div className="bg-white rounded-2xl shadow-float overflow-hidden" data-id="element-824">
              {/* Video placeholder */}
              <div className="relative bg-gradient-to-br from-medical-blue to-medical-blue-dark aspect-video flex items-center justify-center group cursor-pointer" data-id="element-825">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-10" data-id="element-826">
                  <div className="absolute top-4 left-4 w-32 h-32 border-2 border-white rounded-full" data-id="element-827" />
                  <div className="absolute bottom-4 right-4 w-24 h-24 border-2 border-white rounded-full" data-id="element-828" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 border border-white rounded-full" data-id="element-829" />
                </div>

                {/* Founder avatar placeholder */}
                <div className="relative z-10 text-center" data-id="element-830">
                  <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 border-2 border-white/40 overflow-hidden shadow-lg" data-id="element-831">
                    <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&auto=format&fit=crop" alt="Ese Uko" className="w-full h-full object-cover" data-id="element-832" />
                  </div>
                  <div className="text-white font-bold text-lg" data-id="element-833">Ese Uko</div>
                  <div className="text-white/70 text-sm" data-id="element-834">
                    Co-Founder & Chief Recruitment Officer, Care Nomad
                  </div>
                </div>

                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors" data-id="element-835">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform" data-id="element-836">
                    <PlayCircleIcon className="w-8 h-8 text-medical-blue ml-0.5" data-id="element-837" />
                  </div>
                </div>

                {/* Duration badge */}
                <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-lg font-medium" data-id="element-838">
                  3:42
                </div>
              </div>

              {/* Founder quote */}
              <div className="p-6" data-id="element-839">
                <blockquote className="text-text-dark leading-relaxed italic mb-4" data-id="element-840">
                  "I spent 12 years as a bedside nurse watching my colleagues
                  burn out because agencies treated them like commodities. Care
                  Nomad gives healthcare workers the dignity, flexibility, and
                  pay they deserve — while giving facilities the reliable staff
                  they need."
                </blockquote>
                <div className="flex items-center gap-3" data-id="element-841">
                  <div className="w-10 h-10 rounded-full overflow-hidden border border-soft-teal" data-id="element-842">
                    <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=100&auto=format&fit=crop" alt="Ese Uko" className="w-full h-full object-cover" data-id="element-843" />
                  </div>
                  <div data-id="element-844">
                    <div className="font-bold text-text-dark text-sm" data-id="element-845">
                      Ese Uko
                    </div>
                    <div className="text-text-muted text-xs" data-id="element-846">
                      Co-Founder · Chief Recruitment Officer
                    </div>
                  </div>
                  <div className="ml-auto" data-id="element-847">
                    <div className="flex items-center gap-1 text-xs text-text-muted" data-id="element-848">
                      <MapPinIcon className="w-3 h-3" data-id="element-849" />
                      Toronto, ON
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Trust pillars */}
          <div className="space-y-5" data-id="element-850">
            <div className="bg-white rounded-2xl p-6 shadow-card border border-gray-100" data-id="element-851">
              <div className="flex items-start gap-4" data-id="element-852">
                <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-853">
                  <ShieldCheckIcon className="w-6 h-6 text-accent-teal" data-id="element-854" />
                </div>
                <div data-id="element-855">
                  <h3 className="font-bold text-text-dark mb-1" data-id="element-856">
                    Nurse-Vetted Quality Standard
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed" data-id="element-857">
                    Every worker is screened by a registered nurse — not an HR
                    generalist. We verify credentials, check references, and
                    assess clinical competency before anyone joins the platform.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-card border border-gray-100" data-id="element-858">
              <div className="flex items-start gap-4" data-id="element-859">
                <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-860">
                  <HeartIcon className="w-6 h-6 text-accent-teal" data-id="element-861" />
                </div>
                <div data-id="element-862">
                  <h3 className="font-bold text-text-dark mb-1" data-id="element-863">
                    Worker-First Philosophy
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed" data-id="element-864">
                    Healthcare workers choose their shifts, set their rates, and
                    keep more of what they earn. Better pay and flexibility
                    means better retention and better patient outcomes.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-card border border-gray-100" data-id="element-865">
              <div className="flex items-start gap-4" data-id="element-866">
                <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-867">
                  <AwardIcon className="w-6 h-6 text-accent-teal" data-id="element-868" />
                </div>
                <div data-id="element-869">
                  <h3 className="font-bold text-text-dark mb-1" data-id="element-870">
                    Regulated & Compliant
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed" data-id="element-871">
                    Fully compliant with Ontario's LTCHA, BC's CCALA, and
                    federal healthcare regulations. All workers carry valid
                    CNO/CARNA registration and liability insurance.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-card border border-gray-100" data-id="element-872">
              <div className="flex items-start gap-4" data-id="element-873">
                <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-874">
                  <UsersIcon className="w-6 h-6 text-accent-teal" data-id="element-875" />
                </div>
                <div data-id="element-876">
                  <h3 className="font-bold text-text-dark mb-1" data-id="element-877">
                    Community-Driven Growth
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed" data-id="element-878">
                    A portion of every transaction funds nursing scholarships
                    and mental health support for healthcare workers. When
                    nurses thrive, communities thrive.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Certifications & logos bar */}
        <div className="bg-white rounded-2xl p-6 shadow-card border border-gray-100" data-id="element-879">
          <div className="text-center text-xs font-semibold text-text-muted uppercase tracking-widest mb-6" data-id="element-880">
            Recognized & Regulated By
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6" data-id="element-881">
            <div className="text-center" data-id="element-882">
              <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center mx-auto mb-2" data-id="element-883">
                <span className="text-medical-blue font-bold text-xs" data-id="element-884">CNO</span>
              </div>
              <div className="text-xs text-text-muted font-medium" data-id="element-885">
                College of Nurses of Ontario
              </div>
            </div>
            <div className="text-center" data-id="element-886">
              <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center mx-auto mb-2" data-id="element-887">
                <span className="text-medical-blue font-bold text-xs" data-id="element-888">
                  CARNA
                </span>
              </div>
              <div className="text-xs text-text-muted font-medium" data-id="element-889">
                College & Association of RNs of Alberta
              </div>
            </div>
            <div className="text-center" data-id="element-890">
              <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center mx-auto mb-2" data-id="element-891">
                <span className="text-medical-blue font-bold text-xs" data-id="element-892">
                  BCCNM
                </span>
              </div>
              <div className="text-xs text-text-muted font-medium" data-id="element-893">
                BC College of Nurses & Midwives
              </div>
            </div>
            <div className="text-center" data-id="element-894">
              <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center mx-auto mb-2" data-id="element-895">
                <span className="text-medical-blue font-bold text-xs" data-id="element-896">🍁</span>
              </div>
              <div className="text-xs text-text-muted font-medium" data-id="element-897">
                Canadian Owned & Operated
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}